import React, { createContext, useContext } from "react";
import { useAuth } from "@/context/AuthContext";

const DICT = {
  de: {
    // Nav
    "nav.dashboard": "Dashboard",
    "nav.projekte": "Projekte",
    "nav.kunden": "Kunden",
    "nav.designer": "Designer-Pool",
    "nav.verwaltung": "Verwaltung",
    "nav.profil": "Mein Profil",
    "nav.logout": "Abmelden",
    // Common
    "common.save": "Speichern",
    "common.cancel": "Abbrechen",
    "common.delete": "Löschen",
    "common.close": "Schließen",
    "common.open": "Öffnen",
    "common.download": "Herunterladen",
    "common.upload": "Hochladen",
    "common.new": "Neu",
    "common.back": "Zurück",
    "common.loading": "Laden…",
    "common.saving": "Speichere…",
    "common.all": "Alle",
    "common.hours": "Stunden",
    "common.hour_short": "h",
    "common.actions": "Aktionen",
    // Dashboard
    "dashboard.greeting": "Guten Tag",
    "dashboard.overview": "Übersicht",
    "dashboard.today": "Heute",
    "dashboard.projekte_gesamt": "Projekte gesamt",
    "dashboard.in_arbeit": "In Arbeit",
    "dashboard.wartet_freigabe": "Wartet auf Freigabe",
    "dashboard.in_abstimmung": "In Abstimmung",
    "dashboard.abgeschlossen": "Abgeschlossen",
    "dashboard.stunden_monat": "Stunden im Monat",
    "dashboard.avg_freigabezeit": "Ø Freigabezeit",
    "dashboard.active_projects": "Aktive Projekte",
    "dashboard.no_projects": "Keine Projekte",
    "dashboard.see_all": "Alle ansehen →",
    "dashboard.action_required": "Aktion erforderlich",
    "dashboard.entwuerfe_warten": "Entwurf/Entwürfe warten",
    // Status pills
    "status.angelegt": "Angelegt",
    "status.briefing_versendet": "Briefing versendet",
    "status.in_arbeit": "In Arbeit",
    "status.wartet_auf_freigabe": "Wartet auf Freigabe",
    "status.in_abstimmung": "In Abstimmung",
    "status.pausiert": "Auf Eis",
    "status.abgeschlossen": "Abgeschlossen",
    "status.abgerechnet": "Abgerechnet",
    "status.storniert": "Storniert",
    // Project detail
    "project.tab.ueberblick": "Überblick",
    "project.tab.entwuerfe": "Entwürfe",
    "project.tab.zeiten": "Zeiten",
    "project.tab.abrechnung": "Abrechnung",
    "project.tab.briefing": "Briefing",
    "project.tab.ci": "Kunden-CI",
    "project.progress": "Fortschritt",
    "project.hours_logged": "Stunden erfasst",
    "project.of": "von",
    "project.warn_threshold": "Warnschwelle",
    "project.warn_reached": "Warnschwelle erreicht",
    "project.warn_ok": "Im Rahmen",
    "project.warn_over": "Überschritten",
    "project.eckdaten": "Eckdaten",
    "project.startdatum": "Startdatum",
    "project.deadline": "Deadline",
    "project.deadline_laufend": "laufend",
    "project.bereich": "Bereich",
    "project.designer": "Designer",
    "project.your_condition": "Deine Kondition",
    "project.model": "Modell",
    "project.rate": "Satz",
    "project.amount": "Betrag",
    "project.calc_hours": "Kalkulierte Stunden",
    "project.latest_draft": "Aktueller Entwurf",
    "project.latest_draft_new": "Aktueller Entwurf · noch nicht angesehen",
    "project.latest_draft_seen": "Entwurf gesichtet",
    "project.open_draft": "Entwurf öffnen",
    "project.all_versions": "Alle Versionen",
    // Profile
    "profile.title": "Mein Profil",
    "profile.role": "Designer",
    "profile.name": "Name",
    "profile.email": "E-Mail",
    "profile.phone": "Telefon",
    "profile.address": "Adresse",
    "profile.birth_year": "Geburtsjahr",
    "profile.portfolio": "Portfolio-Link",
    "profile.rate": "Stundensatz (€/h)",
    "profile.photo_change": "Foto ändern",
    "profile.language": "Sprache",
    "profile.areas": "Bereiche",
    "profile.areas_hint": "In welchen Bereichen arbeitest du?",
    "profile.services": "Leistungen",
    "profile.services_hint": "Auswahl aus Admin-Katalog",
    "profile.tools": "Tools",
    "profile.tools_hint": "Meine Werkzeuge",
    "profile.references": "Referenzen",
    "profile.references_hint": "Sichtbar im Designer-Pool",
    "profile.add_ref": "Bild hinzufügen",
    "profile.saved": "Profil gespeichert. Die zuständigen PMs wurden benachrichtigt.",
    "profile.save_error": "Fehler beim Speichern.",
    // Time
    "time.form_title": "Zeit erfassen",
    "time.date": "Datum",
    "time.hours": "Stunden",
    "time.activity": "Tätigkeit",
    "time.save": "Eintrag speichern",
    "time.no_entries": "Keine Einträge",
    // Login
    "login.title": "Anmelden",
    "login.invite_only": "Zugang nur per Einladung",
    "login.email": "E-Mail",
    "login.password": "Passwort",
    "login.submit": "Anmelden",
    "login.submitting": "Anmelden…",
    "login.hero": "Ein Ort für <br/>Briefings, Zeiten <br/>und Abrechnung.",
    "login.subtitle": "Für Projektmanager und externe Designer. Klar strukturiert, in Deutsch, ohne Ballast.",
    // Language names
    "lang.de": "Deutsch",
    "lang.en": "Englisch",
    // Table headers / common labels
    "col.title": "Titel",
    "col.client": "Kunde",
    "col.designer": "Designer",
    "col.area": "Bereich",
    "col.deadline": "Deadline",
    "col.status": "Status",
    "col.date": "Datum",
    "col.hours": "Stunden",
    "col.activity": "Tätigkeit",
    "col.version": "Version",
    "col.file": "Datei",
    "col.notes": "Notiz",
    // Projekte page
    "projekte.overview": "Übersicht",
    "projekte.title": "Projekte",
    "projekte.new": "Neues Projekt",
    "projekte.tab_active": "Aktive Projekte",
    "projekte.tab_archive": "Archiv",
    "projekte.no_active": "Keine aktiven Projekte",
    "projekte.no_archived": "Keine archivierten Projekte",
    "projekte.archived_since": "Im Archiv seit",
    // Notifications
    "notif.title": "Benachrichtigungen",
    "notif.mark_all": "Alle gelesen",
    "notif.empty": "Keine Benachrichtigungen",
    "notif.warnschwelle": "Warnschwelle",
    "notif.entwurf_upload": "Neuer Entwurf",
    "notif.freigabe_ergebnis": "Freigabe-Ergebnis",
    "notif.designer_profil_update": "Profil-Update",
    "notif.chat": "Neue Nachricht",
    "notif.new_activity": "Neue Aktivität",
    "notif.threshold_reached": "Warnschwelle {schwelle}% erreicht ({ist}%)",
    // Chat widget
    "chat.title": "Projekt-Chat",
    "chat.saved_hint": "gespeichert im Projekt",
    "chat.unread_count": "{n} neue Nachrichten",
    "chat.unread_one": "1 neue Nachricht",
    "chat.select_project_hint": "Bitte oben ein Projekt auswählen, um zu chatten.",
    "chat.no_messages": "Noch keine Nachrichten. Schreibe die erste!",
    "chat.pick_project": "Projekt wählen …",
    "chat.no_active_projects": "Keine aktiven Projekte",
    "chat.no_projects": "Keine Projekte verfügbar",
    "chat.input_placeholder": "Nachricht schreiben …",
    "chat.input_placeholder_no_project": "Erst Projekt wählen …",
    "chat.send": "Senden",
    "chat.count_messages": "{n} Nachrichten · gespeichert im Projekt",
    "chat.count_message_one": "1 Nachricht · gespeichert im Projekt",
    // Projekt Detail (tabs already exist above)
    "detail.action_menu": "Lifecycle",
    "detail.chat_tab": "Chat",
    // Entwürfe tab
    "entwuerfe.title": "Entwürfe & Freigabe",
    "entwuerfe.upload": "Entwurf hochladen",
    "entwuerfe.new_version": "Neue Version hochladen",
    "entwuerfe.approve": "Freigeben",
    "entwuerfe.request_changes": "Korrektur anfordern",
    "entwuerfe.download": "Download",
    "entwuerfe.no_drafts": "Noch keine Entwürfe hochgeladen.",
    "entwuerfe.status.eingereicht": "Eingereicht",
    "entwuerfe.status.freigegeben": "Freigegeben",
    "entwuerfe.status.korrektur_erforderlich": "Korrektur erforderlich",
    "entwuerfe.approved_on": "Freigegeben am",
    "entwuerfe.uploaded_on": "Hochgeladen am",
    "entwuerfe.history": "Freigabe-Historie",
    // Zeiten tab
    "zeiten.history": "Zeiterfassungen",
    "zeiten.billed": "abgerechnet",
    "zeiten.total": "Summe",
    "zeiten.billed_flag_short": "abgerechnet",
    // Briefing tab
    "briefing.title": "Briefing",
    "briefing.version": "Version",
    "briefing.current": "aktuell",
    "briefing.background": "Ausgangslage",
    "briefing.goal": "Ziel",
    "briefing.audience": "Zielgruppe",
    "briefing.ci": "CI-Vorgaben",
    "briefing.formats": "Formate",
    "briefing.change_note": "Änderungsnotiz",
    "briefing.image": "Bild",
    "briefing.no_briefing": "Noch kein Briefing hinterlegt.",
    // Kunden-CI tab
    "ci.title": "Kunden-CI",
    "ci.no_files": "Keine CI-Dateien hinterlegt.",
    "ci.customer_note": "Anmerkung des Kunden",
    // Overview cards
    "overview.progress": "Fortschritt",
    "overview.threshold_reached": "Warnschwelle erreicht",
    "overview.in_range": "Im Rahmen",
    "overview.exceeded": "Überschritten",
    "overview.hours_of": "{h} h von {t} h",
    "overview.calc_hours_short": "kalkuliert",
    "overview.open_drafts_tab": "Zu den Entwürfen →",
  },
  en: {
    "nav.dashboard": "Dashboard",
    "nav.projekte": "Projects",
    "nav.kunden": "Clients",
    "nav.designer": "Designer Pool",
    "nav.verwaltung": "Administration",
    "nav.profil": "My Profile",
    "nav.logout": "Sign out",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.close": "Close",
    "common.open": "Open",
    "common.download": "Download",
    "common.upload": "Upload",
    "common.new": "New",
    "common.back": "Back",
    "common.loading": "Loading…",
    "common.saving": "Saving…",
    "common.all": "All",
    "common.hours": "Hours",
    "common.hour_short": "h",
    "common.actions": "Actions",
    "dashboard.greeting": "Hello",
    "dashboard.overview": "Overview",
    "dashboard.today": "Today",
    "dashboard.projekte_gesamt": "Total projects",
    "dashboard.in_arbeit": "In progress",
    "dashboard.wartet_freigabe": "Awaiting approval",
    "dashboard.in_abstimmung": "In review",
    "dashboard.abgeschlossen": "Completed",
    "dashboard.stunden_monat": "Hours this month",
    "dashboard.avg_freigabezeit": "Avg. approval time",
    "dashboard.active_projects": "Active projects",
    "dashboard.no_projects": "No projects",
    "dashboard.see_all": "See all →",
    "dashboard.action_required": "Action required",
    "dashboard.entwuerfe_warten": "draft(s) awaiting review",
    "status.angelegt": "Created",
    "status.briefing_versendet": "Briefing sent",
    "status.in_arbeit": "In progress",
    "status.wartet_auf_freigabe": "Awaiting approval",
    "status.in_abstimmung": "In review",
    "status.pausiert": "On hold",
    "status.abgeschlossen": "Completed",
    "status.abgerechnet": "Invoiced",
    "status.storniert": "Cancelled",
    "project.tab.ueberblick": "Overview",
    "project.tab.entwuerfe": "Drafts",
    "project.tab.zeiten": "Hours",
    "project.tab.abrechnung": "Billing",
    "project.tab.briefing": "Briefing",
    "project.tab.ci": "Client CI",
    "project.progress": "Progress",
    "project.hours_logged": "hours logged",
    "project.of": "of",
    "project.warn_threshold": "Warning threshold",
    "project.warn_reached": "Threshold reached",
    "project.warn_ok": "On track",
    "project.warn_over": "Exceeded",
    "project.eckdaten": "Key facts",
    "project.startdatum": "Start date",
    "project.deadline": "Deadline",
    "project.deadline_laufend": "ongoing",
    "project.bereich": "Area",
    "project.designer": "Designer",
    "project.your_condition": "Your condition",
    "project.model": "Model",
    "project.rate": "Rate",
    "project.amount": "Amount",
    "project.calc_hours": "Calculated hours",
    "project.latest_draft": "Latest draft",
    "project.latest_draft_new": "Latest draft · not viewed yet",
    "project.latest_draft_seen": "Draft viewed",
    "project.open_draft": "Open draft",
    "project.all_versions": "All versions",
    "profile.title": "My profile",
    "profile.role": "Designer",
    "profile.name": "Name",
    "profile.email": "Email",
    "profile.phone": "Phone",
    "profile.address": "Address",
    "profile.birth_year": "Year of birth",
    "profile.portfolio": "Portfolio link",
    "profile.rate": "Hourly rate (€/h)",
    "profile.photo_change": "Change photo",
    "profile.language": "Language",
    "profile.areas": "Areas",
    "profile.areas_hint": "Which areas do you work in?",
    "profile.services": "Services",
    "profile.services_hint": "Selection from admin catalog",
    "profile.tools": "Tools",
    "profile.tools_hint": "My tools",
    "profile.references": "References",
    "profile.references_hint": "Visible in the designer pool",
    "profile.add_ref": "Add image",
    "profile.saved": "Profile saved. The responsible PMs have been notified.",
    "profile.save_error": "Error while saving.",
    "time.form_title": "Log time",
    "time.date": "Date",
    "time.hours": "Hours",
    "time.activity": "Activity",
    "time.save": "Save entry",
    "time.no_entries": "No entries",
    "login.title": "Sign in",
    "login.invite_only": "Access by invitation only",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.submitting": "Signing in…",
    "login.hero": "One place for <br/>briefings, hours <br/>and billing.",
    "login.subtitle": "For project managers and external designers. Clear, minimal, focused.",
    "lang.de": "German",
    "lang.en": "English",
    "col.title": "Title",
    "col.client": "Client",
    "col.designer": "Designer",
    "col.area": "Area",
    "col.deadline": "Deadline",
    "col.status": "Status",
    "col.date": "Date",
    "col.hours": "Hours",
    "col.activity": "Activity",
    "col.version": "Version",
    "col.file": "File",
    "col.notes": "Note",
    "projekte.overview": "Overview",
    "projekte.title": "Projects",
    "projekte.new": "New project",
    "projekte.tab_active": "Active projects",
    "projekte.tab_archive": "Archive",
    "projekte.no_active": "No active projects",
    "projekte.no_archived": "No archived projects",
    "projekte.archived_since": "Archived since",
    "notif.title": "Notifications",
    "notif.mark_all": "Mark all read",
    "notif.empty": "No notifications",
    "notif.warnschwelle": "Threshold",
    "notif.entwurf_upload": "New draft",
    "notif.freigabe_ergebnis": "Approval result",
    "notif.designer_profil_update": "Profile update",
    "notif.chat": "New message",
    "notif.new_activity": "New activity",
    "notif.threshold_reached": "Threshold {schwelle}% reached ({ist}%)",
    "chat.title": "Project chat",
    "chat.saved_hint": "stored in project",
    "chat.unread_count": "{n} new messages",
    "chat.unread_one": "1 new message",
    "chat.select_project_hint": "Please select a project above to chat.",
    "chat.no_messages": "No messages yet. Be the first to write!",
    "chat.pick_project": "Choose project …",
    "chat.no_active_projects": "No active projects",
    "chat.no_projects": "No projects available",
    "chat.input_placeholder": "Type a message …",
    "chat.input_placeholder_no_project": "Choose a project first …",
    "chat.send": "Send",
    "chat.count_messages": "{n} messages · stored in project",
    "chat.count_message_one": "1 message · stored in project",
    "detail.action_menu": "Lifecycle",
    "detail.chat_tab": "Chat",
    "entwuerfe.title": "Drafts & Approval",
    "entwuerfe.upload": "Upload draft",
    "entwuerfe.new_version": "Upload new version",
    "entwuerfe.approve": "Approve",
    "entwuerfe.request_changes": "Request changes",
    "entwuerfe.download": "Download",
    "entwuerfe.no_drafts": "No drafts uploaded yet.",
    "entwuerfe.status.eingereicht": "Submitted",
    "entwuerfe.status.freigegeben": "Approved",
    "entwuerfe.status.korrektur_erforderlich": "Changes requested",
    "entwuerfe.approved_on": "Approved on",
    "entwuerfe.uploaded_on": "Uploaded on",
    "entwuerfe.history": "Approval history",
    "zeiten.history": "Time entries",
    "zeiten.billed": "billed",
    "zeiten.total": "Total",
    "zeiten.billed_flag_short": "billed",
    "briefing.title": "Briefing",
    "briefing.version": "Version",
    "briefing.current": "current",
    "briefing.background": "Background",
    "briefing.goal": "Goal",
    "briefing.audience": "Audience",
    "briefing.ci": "CI guidelines",
    "briefing.formats": "Formats",
    "briefing.change_note": "Change note",
    "briefing.image": "Image",
    "briefing.no_briefing": "No briefing available yet.",
    "ci.title": "Client CI",
    "ci.no_files": "No CI files available.",
    "ci.customer_note": "Client note",
    "overview.progress": "Progress",
    "overview.threshold_reached": "Threshold reached",
    "overview.in_range": "On track",
    "overview.exceeded": "Exceeded",
    "overview.hours_of": "{h} h of {t} h",
    "overview.calc_hours_short": "calculated",
    "overview.open_drafts_tab": "Go to drafts →",
  },
};

const LangCtx = createContext({ lang: "de", t: (k) => k });

export function LangProvider({ children }) {
  const { user } = useAuth() || {};
  const lang = user?.sprache === "en" ? "en" : "de";
  const t = (key) => (DICT[lang] && DICT[lang][key]) || DICT.de[key] || key;
  return <LangCtx.Provider value={{ lang, t }}>{children}</LangCtx.Provider>;
}

export const useT = () => useContext(LangCtx).t;
export const useLang = () => useContext(LangCtx).lang;
